import { ColorsType, tokens } from '@news-mono/design-tokens'
import { Product } from '@news-mono/web-common'
import { FallbackImages } from '../__fallback-images'
import { Fonts, fonts } from '../__styling/settings/fonts'
import {
    CardMargins,
    ComponentMetrics,
    metrics,
    SiteMetrics,
    ThemeMargins,
} from '../__styling/settings/metrics'
import {
    get190YearsThemeValues,
    getBauhausDefaultThemeValues,
    getBestAustralianYarnTheme,
    getBusinessThemeValues,
    getCNBCThemeValues,
    getCommonwealthGamesValues,
    getDevelopingNewsTheme,
    getEntertainmentThemeValues,
    getGrandFinalFreoValues,
    getGrandFinalThemeValues,
    getKingsCoronationTheme,
    getLifestyleThemeValues,
    getPoliticsThemeValues,
    getSportThemeValues,
    getStreamerScreamerThemeValues,
    getTechThemeValues,
    getTheEconomistThemeValues,
    getWorldThemeValues,
} from './feature-theme-values'
import { PNBorderRadius } from './settings/borderRadius'
import { sections } from './settings/sections'
import { PerthNowSectionValues } from './settings/sections/perthnow'
import { SevenNewsSectionValues } from './settings/sections/sevennews'
import { TheWestSectionValues } from './settings/sections/thewest'
import {
    CommonZIndex,
    PerthNowZindex,
    SevenNewsZindex,
    TheWestZindex,
    zIndex,
} from './settings/z-index'

export interface Theme {
    fallbackImages: FallbackImages
    kind: Product
    fonts: Fonts
    margins: ThemeMargins
    cards: CardMargins
    componentMetrics: ComponentMetrics
    siteMetrics: SiteMetrics
    section: string
    sectionValues:
        | TheWestSectionValues
        | PerthNowSectionValues
        | SevenNewsSectionValues
    zIndex: (TheWestZindex | PerthNowZindex | SevenNewsZindex) & CommonZIndex
    baseFontSize: number
    colors: ColorsType
    radius?: PNBorderRadius
}

type EmotionTheme = Theme

declare module '@emotion/react' {
    export interface Theme extends EmotionTheme {}
}

export const thewest: Theme = {
    kind: Product.TheWest,
    fonts: fonts.thewest,
    margins: metrics.thewest.margins,
    cards: metrics.thewest.cards,
    componentMetrics: metrics.thewest.componentMetrics,
    siteMetrics: metrics.thewest.siteMetrics,
    zIndex: { ...zIndex.thewest, ...zIndex.common },

    // This following properties are provided through route resolution in provide-route-resolved-data-to-theme.tsx
    fallbackImages: undefined as any,
    section: 'default',
    sectionValues: sections.thewest.default,
    baseFontSize: metrics.thewest.baseFontSize,
    colors: tokens.thewest.colors,
}

export const perthnow: Theme = {
    kind: Product.PerthNow,
    fonts: fonts.perthnow,
    margins: metrics.perthnow.margins,
    cards: metrics.perthnow.cards,
    componentMetrics: metrics.perthnow.componentMetrics,
    siteMetrics: metrics.perthnow.siteMetrics,
    zIndex: { ...zIndex.perthnow, ...zIndex.common },

    // This following properties are provided through route resolution in provide-route-resolved-data-to-theme.tsx
    fallbackImages: undefined as any,
    section: 'default',
    sectionValues: sections.perthnow.default,
    baseFontSize: metrics.perthnow.baseFontSize,
    colors: tokens.perthnow.colors,
}

export const sevennews: Theme = {
    kind: Product.SevenNews,
    fonts: fonts.sevennews,
    margins: metrics.sevennews.margins,
    cards: metrics.sevennews.cards,
    componentMetrics: metrics.sevennews.componentMetrics,
    siteMetrics: metrics.sevennews.siteMetrics,
    zIndex: { ...zIndex.sevennews, ...zIndex.common },

    // This following properties are provided through route resolution in provide-route-resolved-data-to-theme.tsx
    fallbackImages: undefined as any,
    section: 'default',
    sectionValues: sections.sevennews.default,
    baseFontSize: metrics.sevennews.baseFontSize,
    colors: tokens.sevennews.colors,
}

export const themes = [thewest, perthnow, sevennews]

export interface ThemeProps {
    theme: Theme
}

export type ThemeOverrideTypes =
    | 'tokyo'
    | 'grand-final'
    | 'cnbc'
    | 'lifestyle'
    | 'entertainment'
    | 'sport'
    | 'tech'
    | 'politics'
    | 'business'
    | 'bauhaus-default'
    | 'world'
    | 'spotlight'
    | 'commonwealth-games'
    | 'grand-final-freo'
    | 'developing-news'
    | 'developing-news-red'
    | 'developing-news-blue'
    | 'best-australian-yarn'
    | 'streamer-screamer'
    | 'the-economist'
    | 'cnbcEconomist'
    | 'kings-coronation'
    | '190-years-of-the-west'

export type ProductTypes = 'perthnow' | 'thewest'

interface getThemeOverrideValuesProps {
    override: ThemeOverrideTypes
    product: Product
}

export function getThemeOverrideValues({
    override,
    product,
}: getThemeOverrideValuesProps) {
    switch (override) {
        case 'grand-final':
            return getGrandFinalThemeValues({ product })
        case 'cnbc':
            return getCNBCThemeValues() //This is only active on The West
        case 'cnbcEconomist':
            return getCNBCThemeValues()
        case 'the-economist':
            return getTheEconomistThemeValues()
        case 'kings-coronation':
            return getKingsCoronationTheme()
        case 'lifestyle':
            return getLifestyleThemeValues()
        case 'entertainment':
            return getEntertainmentThemeValues()
        case 'sport':
            return getSportThemeValues()
        case 'politics':
            return getPoliticsThemeValues()
        case 'tech':
            return getTechThemeValues()
        case 'business':
            return getBusinessThemeValues()
        case 'world':
            return getWorldThemeValues()
        case 'bauhaus-default':
            return getBauhausDefaultThemeValues()
        case 'spotlight':
            return getEntertainmentThemeValues()
        case 'commonwealth-games':
            return getCommonwealthGamesValues()
        case 'grand-final-freo':
            return getGrandFinalFreoValues()
        case 'developing-news':
            return getDevelopingNewsTheme('grey')
        case 'developing-news-red':
            return getDevelopingNewsTheme('red')
        case 'developing-news-blue':
            return getDevelopingNewsTheme('blue')
        case 'best-australian-yarn':
            return getBestAustralianYarnTheme()
        case 'streamer-screamer':
            return getStreamerScreamerThemeValues()
        case '190-years-of-the-west':
            return get190YearsThemeValues()
        default:
            return {}
    }
}
